import { useHistory } from "react-router-dom";
import { ActionType } from "constant";
import ActionLayout from "pages/events/components/actions/ActionLayout";
import SMSActionIcon from "assets/svg/event-action-sms.svg";
import EmailActionIcon from "assets/svg/event-action-email.svg";
import NotificationActionIcon from "assets/svg/event-action-notification.svg";
import { useContext, useEffect, useMemo, useState } from "react";
import { isEmpty } from "lodash";
import { AppContext } from "context/appContext";
import { MemberDto, MemberStatus } from "types";
import { getMemberName } from "utils/organisationFunction";
import OrganisationService from "service/organisationService";
import { isHttpSuccess, orgId } from "utils/functions";

type NotificationActionProps = {
    eventId: string;
    actionId: string;
    showDeleteConfirm: any;
    action: any;
    actionType: string;
    checkEditable: any;
};

const NotificationAction = ({
    eventId,
    actionId,
    showDeleteConfirm,
    action,
    actionType,
    checkEditable,
}: NotificationActionProps) => {
    const history = useHistory();

    const DISPLAY_ACTION_NAMES = {
        [ActionType.EMAIL]: "Email",
        [ActionType.PUSH]: "Notification",
        [ActionType.SMS]: "SMS",
    };

    const {
        storeData: {
            organization: {
                currentOrgInfo: { isDefault },
            },
            user: {
                userData: { phoneNumber },
            },
        },
    } = useContext(AppContext);

    const [orgMembers, setOrgMembers] = useState<MemberDto[]>([]);

    useEffect(() => {
        const fetch = async () => {
            const { status, data = [] } =
                await OrganisationService.getOrgMembers(orgId());
            if (isHttpSuccess(status)) {
                const members = data
                    .toCamelCase<MemberDto>()
                    .filter(
                        (curr: { status: MemberStatus }) =>
                            curr.status !== MemberStatus.Pending
                    );
                setOrgMembers(members);
            }
        };

        fetch();
    }, []);

    const recipients = useMemo(() => {
        if (isEmpty(orgMembers) || isEmpty(action.resource.recipients)) {
            return [];
        }

        const recipientSet = new Set(
            action.resource.recipients.map((recipient: string) =>
                recipient.toLowerCase()
            )
        );

        return orgMembers.filter(
            (member) =>
                member?.username &&
                recipientSet.has(member.username.toLowerCase())
        );
    }, [action.resource.recipients, orgMembers]);

    const renderRecipients = () => {
        if (recipients.length > 1) {
            if (isDefault) {
                return (
                    <h6>
                        To &lsquo;{recipients[0].username}&rsquo; and{" "}
                        {recipients.length - 1} more
                    </h6>
                );
            }
            return (
                <h6>
                    To &lsquo;{getMemberName(recipients[0])}&rsquo; and{" "}
                    {recipients.length - 1} more
                </h6>
            );
        }
        if (action.type_ === ActionType.SMS) {
            if (isDefault) {
                return <h6>To &lsquo;{phoneNumber}&rsquo;</h6>;
            }
        }

        if (isDefault) {
            return <h6>To &lsquo;{recipients[0]?.username}&rsquo;</h6>;
        }
        return <h6>To &lsquo;{getMemberName(recipients[0])}&rsquo;</h6>;
    };

    const renderNotificationContent = () => {
        return (
            <>
                <p className="mt-2 mb-3 default-white-color">
                    {
                        DISPLAY_ACTION_NAMES[
                            actionType as keyof typeof DISPLAY_ACTION_NAMES
                        ]
                    }
                </p>
                <p className="content-desc">{action.resource.content}</p>
                {renderRecipients()}
            </>
        );
    };

    const renderActionIcon = () => {
        if (actionType === ActionType.SMS) {
            return <img src={SMSActionIcon} alt="action-sms-icon" />;
        }
        if (actionType === ActionType.EMAIL) {
            return <img src={EmailActionIcon} alt="action-email-icon" />;
        }
        return <img src={NotificationActionIcon} alt="action-push-icon" />;
    };

    return (
        <ActionLayout
            showDeleteConfirm={showDeleteConfirm}
            editAction={(e) => {
                const isEditable = checkEditable(e);
                if (isEditable) {
                    history.push(
                        `/event-details/${eventId}/action/${DISPLAY_ACTION_NAMES[
                            actionType as keyof typeof DISPLAY_ACTION_NAMES
                        ].toLowerCase()}/${actionId}`
                    );
                }
            }}
            renderActionIcon={renderActionIcon}
        >
            {renderNotificationContent()}
        </ActionLayout>
    );
};

export default NotificationAction;
